import _ from 'underscore';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  OverviewPerformanceTable,
  ISortState,
} from '@bingads-webui-campaign-react/overview-performance-table';
import { useHeaderStateContext } from '@bingads-webui-campaign-react/overview-card';
import { getUIMetrics } from '@bingads-webui-campaign/overview-cards-common';


import { IGridDataTableProps } from './types';
import { useGridData } from './use-grid-data';
import { getOrderFromGridSort } from './order-by-helper';
import { defaultComparisonMetric } from './consts';

export function GridDataTable({
  metricFormatter,
  i18n,
  constructOptions,
  dateRange,
  filter,
  permissions,
  jsdata,
  dateUtil,
  allMetrics,
  initialSelectedMetrics,
  initialSortColumn,
  initialSortDirection,
  patchPreference,
  cardTypeId,
  footerButtonConfigs,
  entityDisPlayName,
  perfMarkerUtils,
  getHref,
  getPopoverContent,
  triggerSearchTermDrawer,
  entityType,
  navigationLevel,
  campaignType,
}: IGridDataTableProps) {
  const [selectedMetrics, setSelectedMetrics] = useState(initialSelectedMetrics);
  const [uiMetrics] = useState(getUIMetrics(allMetrics, campaignType, cardTypeId));
  const [sortState, setSortState] = useState<ISortState>({ sortColumn: initialSortColumn, sortDirection: initialSortDirection });
  const headerState = useHeaderStateContext();
  const comparisonMajorMetric = dateRange.compared ? headerState?.selectedItemValue : defaultComparisonMetric;
  const [isFirstDataLoaded, setIsFirstDataLoaded] = useState(false);
  const {
    willFetchData,
    willRender,
    done,
  } = perfMarkerUtils;

  const {
    orderby,
    direction,
  } = useMemo(() => getOrderFromGridSort({
    sortColumn: sortState.sortColumn,
    sortDirection: sortState.sortDirection,
    dateRange,
    comparisonMajorMetric,
  }), [comparisonMajorMetric, dateRange, sortState]);
  const columns = useMemo(() => selectedMetrics.map(m => m.key), [selectedMetrics]);

  const {
    data,
    isLoading,
  } = useGridData({
    dateRange,
    filter,
    constructOptions,
    permissions,
    jsdata,
    dateUtil,
    orderby,
    direction,
    selectedMenuItem: comparisonMajorMetric,
    columns,
    cardTypeId,
    willFetchData,
    willRender,
    navigationLevel,
  });

  useEffect(() => {
    if (!isFirstDataLoaded && !isLoading) {
      setIsFirstDataLoaded(true);
      done();
    }
  }, [data, done, isFirstDataLoaded, isLoading, willRender]);

  const onChange = useCallback(({
    newSelectedMetrics,
    newSortState,
  }) => {
    if (newSelectedMetrics) {
      setSelectedMetrics(newSelectedMetrics);
      const newSelectedColumns = newSelectedMetrics.map(m => m.key);
      patchPreference({
        selectedColumns: newSelectedColumns,
      });
    }
    if (newSortState) {
      setSortState(newSortState);
      patchPreference(newSortState);
    }
  }, [patchPreference]);

  return (
    <OverviewPerformanceTable
      isLoading={isLoading}
      entityDisPlayName={entityDisPlayName}
      selectedMetrics={selectedMetrics}
      allMetrics={uiMetrics}
      sortState={sortState}
      data={data}
      onChange={onChange}
      i18n={i18n}
      metricFormatter={metricFormatter}
      comparisonMajorMetric={comparisonMajorMetric || 'PerformanceMetrics'}
      footerButtonConfigs={footerButtonConfigs}
      getHref={getHref}
      getPopoverContent={getPopoverContent}
      triggerSearchTermDrawer={triggerSearchTermDrawer}
      entityType={entityType}
    />
  );
}

GridDataTable.defaultProps = {
  footerButtonConfigs: null,
  getPopoverContent: undefined,
};
